<template>
  <div class="wrapper">
    <div
      ref="editor"
      class="editor app-input comment-message-content"
      contenteditable="true"
      @input="onInput"
      @keydown="onKeydown"
      @blur="onBlur"
    ></div>

    <div
      v-if="showDropdown && filteredUsers.length"
      class="dropdown"
      :style="styleDropdown"
    >
      <div
        v-for="(user, index) in filteredUsers"
        :key="user.id"
        :class="{ active: index === activeIndex }"
        class="dropdown-item"
        :data-user-id="user.id"
        @mousedown.prevent="selectUser(user)"
        @mouseover="activeIndex = index"
      >
        {{ user.name }}
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/http-helper';

export default {
  name: 'message-component',

  props: {
    value: {
      type: String,
      required: true,
    },

    topicId: {
      type: Number,
      required: true,
    },

    hideUsersTagging: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showDropdown: false,
      query: '',
      activeIndex: 0,
      styleDropdown: { top: 0, left: 0, right: 0 },

      users: [],

      selectedUsers: [],
    };
  },

  computed: {
    filteredUsers() {
      return this.users.filter(u => u.name.toLowerCase().includes(this.query.toLowerCase()));
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.editor.innerHTML = this.value;
    });
  },

  methods: {
    updateModel() {
      const text = this.$refs.editor.innerHTML;

      this.$emit('input', text);

      this.selectedUsers = new Set(
        Array.from(this.$refs.editor.querySelectorAll('.mention')).map(
          mention => +mention.getAttribute('data-user-id'),
        ),
      );

      this.$emit('update:selectedUsers', Array.from(this.selectedUsers));
    },

    isEmpty() {
      const text = this.$refs.editor.innerText;
      return !text.trim();
    },

    async onInput() {
      if (this.isEmpty()) {
        this.$refs.editor.innerHTML = '';
        this.updateModel();
        return;
      }

      this.updateModel();
      
      if (this.hideUsersTagging) {
        return;
      }

      const text = this.getTextBeforeCursor();
      const match = text.match(/@([\p{L}\p{N}_]*)$/u);

      if (match) {
        this.query = match[1];

        if (!this.query) {
          await this.loadParticipants();
        }

        this.showDropdown = true;

        this.$nextTick(() => {
          const coords = this.getCaretCoordinates();
          if (coords) this.styleDropdown = coords;

          if (this.filteredUsers.length === 1) {
            this.activeIndex = 0;
          }
        });
      } else {
        this.showDropdown = false;
      }
    },

    onBlur() {
      this.showDropdown = false;
    },

    async loadParticipants() {
      if (this.users.length) return;

      const result = await get(`/participant/mention-users?topic_id=${this.topicId}`);

      if (result.status) {
        this.users = Object.keys(result.data)
          .map(key => ({
            id: +key,
            name: result.data[key],
          }))
          .filter(user => user.id !== this.$store.getters['auth/userInfo'].id);
      }
    },

    getTextBeforeCursor() {
      const selection = window.getSelection();
      if (!selection.rangeCount) return '';

      const range = selection.getRangeAt(0);
      const preRange = range.cloneRange();

      preRange.selectNodeContents(this.$refs.editor);
      preRange.setEnd(range.endContainer, range.endOffset);

      return preRange.toString();
    },

    getCaretCoordinates() {
      const selection = window.getSelection();
      if (!selection.rangeCount) return null;

      const range = selection.getRangeAt(0).cloneRange();
      range.collapse(false);

      const rect = range.getClientRects()[0];

      if (!rect) return null;

      const parentRect = this.$refs.editor.getBoundingClientRect();

      const left = rect.left - parentRect.left + window.scrollX;
      const right = parentRect.right - rect.right + window.scrollX;
      const top = rect.bottom - parentRect.top + window.scrollY;
      const bottom = parentRect.bottom - rect.bottom + window.scrollY;

      return {
        right: rect.right > parentRect.right - 100 ? `${right}px` : 'auto',
        left: rect.right > parentRect.right - 100 ? 'auto' : `${left}px`,
        top: rect.bottom > parentRect.bottom - 60 ? 'auto' : `${top}px`,
        bottom: rect.bottom > parentRect.bottom - 60 ? `${bottom}px` : 'auto',
      };
    },

    insertMention(user) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);

      const text = this.getTextBeforeCursor();
      const match = text.match(/@([\p{L}\p{N}_]*)$/u);

      if (match) {
        const len = match[0].length;
        range.setStart(range.endContainer, range.endOffset - len);
        range.deleteContents();
      }

      const span = document.createElement('span');
      span.textContent = `@${user.name}`;
      span.contentEditable = 'false';
      span.className = 'mention';
      span.setAttribute('data-user-id', user.id);

      const space = document.createTextNode('\u00A0');

      range.insertNode(space);
      range.insertNode(span);

      // перенос курсора
      range.setStartAfter(space);
      range.setEndAfter(space);

      selection.removeAllRanges();
      selection.addRange(range);

      this.showDropdown = false;
      this.activeIndex = 0;

      this.updateModel();
    },

    selectUser(user) {
      this.insertMention(user);
    },

    scrollToActiveUser() {
      const activeUser = this.filteredUsers[this.activeIndex];
      if (activeUser) {
        const userElement = document.querySelector(`.dropdown-item[data-user-id="${activeUser.id}"]`);
        if (userElement) {
          userElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    },

    onKeydown(e) {
      if (!this.showDropdown || !this.filteredUsers.length || this.hideUsersTagging) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.filteredUsers.length;
        this.scrollToActiveUser();
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + this.filteredUsers.length) % this.filteredUsers.length;
        this.scrollToActiveUser();
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const user = this.filteredUsers[this.activeIndex];
        if (user) this.insertMention(user);
      }

      if (e.key === 'Escape') {
        this.showDropdown = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  position: relative;
  .editor {
    height: 9rem;
    padding: 0.5rem;
    overflow: auto;
    line-height: 1.5;
    cursor: text;
  }

  .dropdown {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 555;
    width: auto;
    max-height: 120px;
    overflow: auto;
    background: white;
    border: 1px solid $copyGray;
    border-radius: 0.3rem;
    overflow: auto;
    padding: 2px;
    font-size: 12px;
    margin: 0;

    &-item {
      padding: 6px 10px;
      border-radius: 0.3rem;
      cursor: pointer;

      &.active {
        background: $lightGray;
      }
    }
  }
}
</style>
